import {
  MutationFunction,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import {map} from "lodash";

export type BaseMutationOptions<Data, Variables> = Omit<UseMutationOptions<Data, unknown, Variables>, "mutationFn"> & {
  refetchType?: "active" | "inactive" | "all" | "none";
  resetQueriesKeyFn?: (data: Data, variables: Variables) => unknown[];
  prefixKeysToReset?: (data: Data, variables: Variables) => string[][];
  invalidateQueriesKeyFn?: (data: Data, variables: Variables) => unknown[];
  prefixKeysToInvalidate?: (data: Data, variables: Variables) => string[][];
};

export type BaseUseMutationResult<Data, Variables> = UseMutationResult<Data, unknown, Variables>;

export const useBaseMutation = <Data, Variables>(
  mutationFn: MutationFunction<Data, Variables>,
  options?: BaseMutationOptions<Data, Variables>
): BaseUseMutationResult<Data, Variables> => {
  const queryClient = useQueryClient();

  return useMutation<Data, unknown, Variables>(mutationFn, {
    ...options,
    onSuccess: async (data, variables) => {
      const refetchType = options?.refetchType ?? "active";

      if (options?.resetQueriesKeyFn) {
        const keys = map(options.resetQueriesKeyFn(data, variables), (value) => String(value));
        await queryClient.resetQueries(keys);
      }
      if (options?.prefixKeysToReset) {
        options?.prefixKeysToReset(data, variables).forEach((key) => {
          const keys = map(key, (value) => String(value));
          queryClient.resetQueries(keys);
        });
      }
      if (options?.invalidateQueriesKeyFn) {
        const keys = options.invalidateQueriesKeyFn(data, variables);
        for (const key of keys) {
          await queryClient.invalidateQueries({queryKey: [String(key)], refetchType});
        }
      }
      if (options?.prefixKeysToInvalidate) {
        for (const key of options.prefixKeysToInvalidate(data, variables)) {
          const stringKeys = map(key, (value) => String(value));
          await queryClient.invalidateQueries({queryKey: stringKeys, refetchType});
        }
      }
    },
  });
};
